"use client";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

import useLanguage from "@/contexts/language-context";
import { TableCell, TableRow } from "@/components/molecules/Table/styled";
import SWR_KEYS from "@/constants/swr-keys";
import useNotificationData from "@/hooks/data/use-notification-data";
import useMarkAsReadNotification from "@/hooks/requests/notification/use-mark-as-read-notification";
import useMutate from "@/hooks/use-mutate";
import usePopOver from "@/hooks/use-popover";
import INotification from "@/interfaces/notification.interface";
import { NotificationsMessages } from "@/messages/notifications/notifications.messages";

import { NotificationPopover } from "../Popover";

interface Props {
  notification: INotification;
  index: number;
  messages: NotificationsMessages;
}

export function NotificationTableRow({ notification, index, messages }: Props) {
  const data = useNotificationData({ notification, messages });
  const { openPopover, anchorEl, closePopover, isOpen, selected } = usePopOver<INotification>();
  const { markAsRead } = useMarkAsReadNotification();
  const { mutate } = useMutate();
  const { lang } = useLanguage();
  const router = useNavigate();

  const onSuccess = () => mutate(SWR_KEYS.getNotifications);

  const onCheckboxClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const successMessage = messages.markSingleNotificationAsReadSuccess;
    const errorMessage = messages.markAllNotificationsAsReadError;

    if (!notification.read) {
      await markAsRead({ _id: Number(notification.id), successMessage, errorMessage, onSuccess });
    }
  };

  const handleOpenDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (notification.link) {
      // Para links de imports, envia apenas para a rota base /imports/PT-BR
      if (notification.link.includes("/imports/")) {
        router(`/imports/${lang}`);
      } else {
        // Para outros links, remove o domínio e mantém o path
        const path = notification.link.replace(/^(?:https?:\/\/[^/]+)?(.*)$/, "$1");
        router(path);
      }
    } else {
      router(`/notifications/${lang}/${notification.id}`);
    }
  };

  const MAX_DETAILS_LENGTH = 100;
  const truncatedDetails =
    data.details.length > MAX_DETAILS_LENGTH
      ? data.details.substring(0, MAX_DETAILS_LENGTH) + "..."
      : data.details;

  return (
    <>
      <TableRow index={index}>
        <TableCell>
          <Checkbox checked={notification.read} onClick={onCheckboxClick} />
        </TableCell>
        <TableCell>{data.formattedDateTime}</TableCell>
        <TableCell onClick={handleOpenDetails} style={{ cursor: "pointer" }}>
          {truncatedDetails}
        </TableCell>
        <TableCell>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              openPopover(e, notification);
            }}
          >
            <MoreHorizIcon />
          </IconButton>

          {selected && (
            <NotificationPopover
              isOpen={isOpen}
              anchorEl={anchorEl}
              onClose={closePopover}
              messages={messages}
              selected={selected}
            />
          )}
        </TableCell>
      </TableRow>
    </>
  );
}
